/**
 * Tenant-level Script Library access policy.
 *
 * Two-axis gate: a user can see the Script Library if their permission role
 * OR their free-text department is allow-listed. Admins are *always* allowed
 * regardless of the configuration — they need the library to manage it.
 *
 * Default is "open to all" (no enforcement) so existing tenants are not
 * disrupted by the upgrade. An admin must explicitly enable enforcement and
 * choose which roles/departments may access the library.
 *
 * Storage: lives inside the tenant-wide GLOBAL user-preferences document
 * under the `scriptLibraryAccess` key, written via the existing
 * `/users/preferences` endpoint with backend admin-role gating.
 */

export interface ScriptLibraryAccessConfig {
  /**
   * When false, every authenticated user in the tenant can see the library
   * (the lists below are ignored). When true, only admins + users whose role
   * or department is in the allow-lists can see it.
   */
  enforced: boolean;
  /** Lowercase permission-role names allowed (excluding admin, which is implicit). */
  allowedRoles: string[];
  /** Lowercase, trimmed department names allowed (matched against `user.department`). */
  allowedDepartments: string[];
}

export const DEFAULT_SCRIPT_LIBRARY_ACCESS: ScriptLibraryAccessConfig = {
  enforced: false,
  allowedRoles: [],
  allowedDepartments: [],
};

/** Permission roles that admins can choose to expose the library to. */
export const SCRIPT_LIBRARY_ELIGIBLE_ROLES: ReadonlyArray<{
  id: string;
  label: string;
  description: string;
}> = [
  {
    id: 'manager',
    label: 'Manager',
    description: 'Manages campaigns, domains, and email accounts.',
  },
  {
    id: 'operator',
    label: 'Operator',
    description: 'Operates campaigns and manages email accounts.',
  },
  {
    id: 'viewer',
    label: 'Viewer',
    description: 'View-only access to campaigns and analytics.',
  },
];

/** Normalize an arbitrary raw value off the preferences blob into a usable config. */
export function normalizeScriptLibraryAccessConfig(
  raw: unknown
): ScriptLibraryAccessConfig {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SCRIPT_LIBRARY_ACCESS };
  const r = raw as Record<string, unknown>;
  const enforced = Boolean(r.enforced);
  const rolesRaw = Array.isArray(r.allowedRoles) ? r.allowedRoles : [];
  const deptsRaw = Array.isArray(r.allowedDepartments) ? r.allowedDepartments : [];
  return {
    enforced,
    allowedRoles: rolesRaw
      .map((x) => String(x || '').trim().toLowerCase())
      .filter(Boolean),
    allowedDepartments: deptsRaw
      .map((x) => String(x || '').trim().toLowerCase())
      .filter(Boolean),
  };
}

/**
 * Return true if the user can see the Script Library given the tenant config.
 * Admins always pass. When the config is undefined or enforcement is off,
 * everyone passes.
 */
export function canSeeScriptLibrary(
  user: { role?: string | null; department?: string | null } | null | undefined,
  config: ScriptLibraryAccessConfig | undefined
): boolean {
  const role = String(user?.role || '').trim().toLowerCase();
  if (role === 'admin') return true;
  const cfg = config || DEFAULT_SCRIPT_LIBRARY_ACCESS;
  if (!cfg.enforced) return true;
  if (role && cfg.allowedRoles.includes(role)) return true;
  const dept = String(user?.department || '').trim().toLowerCase();
  if (dept && cfg.allowedDepartments.includes(dept)) return true;
  return false;
}
